import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getOrganization } from '../utils/organizationApi';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Organization type - making it compatible with both auth systems
interface Organization {
  id: string;
  name: string;
  display_name?: string;
  plan?: string;
  status?: string;
  companyEmail?: string;
  metadata?: Record<string, unknown>;
}

// Organization context interface
interface OrganizationContextType {
  currentOrganization: Organization | null;
  availableOrganizations: Organization[];
  isLoadingOrganizations: boolean;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  canSwitchOrganizations: boolean;
  organizationError: string | null;
}

// Create the context
const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Organization Provider
export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth() as {user?: {id?: string}; isAuthenticated?: boolean};
  const { user, isAuthenticated } = auth;
  
  // Local state for current organization
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

  // These might not be available in mock auth
  const authSwitchOrg = auth.switchOrganization || (() => Promise.resolve());
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(null);

  // Load current organization from API (use auth context data for consistency)
  const refreshCurrentOrganization = useCallback(async () => {
    if (!isAuthenticated || !user?.orgId) {
      setCurrentOrganization(null);
      return;
    }
    try {
      setOrganizationError(null);

      // Use the user data from auth context since it's already loaded correctly
      if (user) {
        const org: Organization = {
          id: user.orgId,
          name: user.organizationName || 'Unknown Organization',
          plan: user.plan,
          status: 'active',
          companyEmail: undefined
        };
        setCurrentOrganization(org);
        return;
      }

      // If for some reason user data is incomplete, try API fallback
      const orgDetails = await getOrganization(user.orgId);
      if (orgDetails.success && orgDetails.data) {
        setCurrentOrganization(orgDetails.data);
      } else {
        setCurrentOrganization(null);
        setOrganizationError(orgDetails.error?.message || 'Failed to load organization');
      }
    } catch (error) {
      console.error('Failed to load current organization:', error);
      setCurrentOrganization(null);
      setOrganizationError(error instanceof Error ? error.message : 'Failed to load organization');
    }
  }, [isAuthenticated, user]);

  // Load available organizations (simplified since user typically has one org)
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setAvailableOrganizations([]);
      return;
    }

    try {
      setIsLoadingOrganizations(true);
      setOrganizationError(null);

      // For now, just use the current user's organization
      // In the future, this could be expanded to support multiple organizations per user
      if (user.orgId && user.organizationName) {
        const orgs: Organization[] = [{
          id: user.orgId,
          name: user.organizationName,
          plan: user.plan,
          status: 'active',
          companyEmail: undefined
        }];
        setAvailableOrganizations(orgs);
      } else {
        setAvailableOrganizations([]);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizationError(error instanceof Error ? error.message : 'Failed to load organizations');
      setAvailableOrganizations([]);
    } finally {
      setIsLoadingOrganizations(false);
    }
  }, [isAuthenticated, user]);

  // Switch organization with error handling
  const switchOrganization = useCallback(async (orgId: string) => {
    try {
      setOrganizationError(null);
      await authSwitchOrg(orgId);
      
      // Refresh organizations list after switch
      await refreshOrganizations();
      await refreshCurrentOrganization();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      setOrganizationError(error instanceof Error ? error.message : 'Failed to switch organization');
      throw error;
    }
  }, [authSwitchOrg, refreshOrganizations, refreshCurrentOrganization]);

  // Load organizations when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshOrganizations();
      refreshCurrentOrganization();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]); // Only depend on auth state, not the function itself

  // Determine if user can switch organizations
  const canSwitchOrganizations = availableOrganizations.length > 1;

  const contextValue: OrganizationContextType = useMemo(() => ({
    currentOrganization,
    availableOrganizations,
    isLoadingOrganizations,
    switchOrganization,
    refreshOrganizations,
    canSwitchOrganizations,
    organizationError
  }), [
    currentOrganization,
    availableOrganizations,
    isLoadingOrganizations,
    switchOrganization,
    refreshOrganizations,
    canSwitchOrganizations,
    organizationError
  ]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};

// Custom hook to use the organization context
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

// Hook to get organization-scoped data
export const useOrganizationScope = () => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  return {
    orgId: currentOrganization?.id || user?.orgId,
    orgName: currentOrganization?.name,
    orgPlan: currentOrganization?.plan || user?.plan,
    orgStatus: currentOrganization?.status,
    hasOrganization: !!(currentOrganization?.id || user?.orgId),
    isOrgActive: currentOrganization?.status === 'active'
  };
};

export default OrganizationProvider;
